/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useState } from 'react'
import { Box, Typography } from '@mui/material'
import { useStytchMember } from '@stytch/react/b2b'
import {
  DocWithOptionalProjectId,
  useDeleteDocuments,
  useRegisterDocuments,
} from '@/service-library/hooks/documents'
import queryKeys from '@/service-library/query-keys'
import uploadToS3 from '@/services/upload-to-s3'
import { Organization } from '@/types/organizations'
import { showErrorSnackbar } from '@/utils/snackbars'
import generateUuid from '@/utils/uuid'

const MAX_NAMES_SHOWN = 5

function showFailedUploadsSnackbar(fileNames: string[]) {
  const remaining = fileNames.length - MAX_NAMES_SHOWN
  showErrorSnackbar(
    <Box>
      <Typography variant="body2">
        {fileNames.length === 1
          ? 'This file failed to upload:'
          : `${fileNames.length} files failed to upload:`}
      </Typography>
      <Box component="ul" sx={{ my: 0.5, pl: 2.5 }}>
        {fileNames.slice(0, MAX_NAMES_SHOWN).map((name, index) => (
          <li key={index}>{name}</li>
        ))}
        {remaining > 0 && <li>and {remaining} more</li>}
      </Box>
      <Typography variant="body2">Please try uploading again.</Typography>
    </Box>,
    { autoHideDuration: 5000 },
  )
}

export default function useUploadFiles({
  workflowId,
}: {
  workflowId?: string
}) {
  const { member } = useStytchMember()
  const [numberToUpload, setNumberToUpload] = useState(0)
  const { registerDocumentsAsync, error } = useRegisterDocuments()
  const { deleteDocumentsAsync } = useDeleteDocuments({
    sideEffectQueryKeys: [
      queryKeys.documents.lists(),
      queryKeys.documentRows.lists(),
      queryKeys.documentWorkflowStates.lists(),
    ],
  })

  async function uploadFiles({
    orgId,
    files,
    projectId,
  }: {
    projectId?: string
    orgId: string
    files: FileList | File[]
  }) {
    const allDocuments: DocWithOptionalProjectId[] = []
    const failedUploads: { id: string; name: string }[] = []
    const fileToDocumentObjects = Array.from(files).map((file) => ({
      file,
      document_id: generateUuid(),
    }))

    setNumberToUpload((prev) =>
      prev >= 0 // If for some reason we ever end up with a negative number, this would fix it
        ? prev + fileToDocumentObjects.length
        : fileToDocumentObjects.length,
    )

    // Split file objects into batches of 15
    const batches: {
      file: File
      document_id: string
    }[][] = []
    for (let i = 0; i < fileToDocumentObjects.length; i += 15) {
      batches.push(fileToDocumentObjects.slice(i, i + 15))
    }

    async function processBatch(batch: DocWithOptionalProjectId[]) {
      const batchFailures: { id: string; name: string }[] = []
      const promises = batch.map(async (document) => {
        if (!document) return
        const fileToDoc = fileToDocumentObjects.find(
          (fileToDoc) => fileToDoc.document_id === document.id,
        )
        return uploadToS3(document.upload_file_url!, fileToDoc!.file)
          .then(() => {
            setNumberToUpload((prev) => prev - 1)
          })
          .catch((err) => {
            console.error(err)
            batchFailures.push({
              id: document.id,
              name: fileToDoc!.file.name,
            })
          })
      })
      await Promise.all(promises)

      if (batchFailures.length === 0) return
      // These documents were registered, but their files never reached S3.
      // Nothing will ever process them, so delete them.
      // Otherwise they would show as "In Flight" forever.
      // FileUploadProvider warns before the page closes while numberToUpload
      // is above 0. So the failed files stay in that count until the delete
      // is done, and closing the tab can't cut the delete off.
      await deleteDocumentsAsync(batchFailures.map(({ id }) => id)).catch(
        (err) => {
          console.error(err)
        },
      )
      failedUploads.push(...batchFailures)
      setNumberToUpload((prev) => prev - batchFailures.length)
    }

    let error = false
    for (let i = 0; i < batches.length; i++) {
      const batch = batches[i]
      try {
        const documents = await registerDocumentsAsync(
          batch.map((fileToDoc) => ({
            name: fileToDoc.file.name,
            owner_org_id: orgId,
            project_id: projectId,
            submitter: member?.name,
            submitter_email: member?.email_address,
            id: fileToDoc.document_id,
            workflow_id: workflowId,
          })),
        )
        allDocuments.push(...documents)
        await processBatch(documents)
      } catch {
        setNumberToUpload((prev) => prev - batch.length)
        error = true
      }
    }

    error &&
      showErrorSnackbar(
        'Some uploads may have failed. If this problem persists, please contact Pixydocs support.',
      )

    if (failedUploads.length > 0) {
      const failedIds = new Set(failedUploads.map(({ id }) => id))
      showFailedUploadsSnackbar(failedUploads.map(({ name }) => name))
      return allDocuments.filter(({ id }) => !failedIds.has(id))
    }

    return allDocuments
  }

  function uploadFilesAndSetOrgs({
    projectId,
    org,
    files,
  }: {
    projectId?: string
    org: Organization
    files: FileList | File[]
  }) {
    return uploadFiles({
      projectId,
      orgId: org.id,
      files,
    })
  }

  return {
    uploadFiles,
    uploadFilesAndSetOrgs,
    numberToUpload,
    error,
  }
}
